/** A terceira coluna do AppShell: à direita da coluna principal, irmã dela,
 *  fora do Outlet. Por isso sobrevive à navegação e a conversa continua de uma
 *  tela para outra. Empurra o conteúdo, não sobrepõe: o usuário segue operando
 *  a tela enquanto conversa.
 *
 *  Em md ou mais é a coluna com a mesma transição de largura da barra lateral e
 *  uma alça de arraste na borda esquerda. Abaixo de md não há o que empurrar: vira
 *  gaveta temporária de largura total, como a navegação em celular. */
import { useEffect, useRef, useState, type KeyboardEvent, type PointerEvent, type TransitionEvent } from 'react'
import Box from '@mui/material/Box'
import Drawer from '@mui/material/Drawer'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import { useI18n } from '@/core/i18n/useI18n'
import { LARGURA_MAX, LARGURA_MIN, limitarLargura, useAssistente } from './AssistenteProvider'
import { AssistentePanel } from './AssistentePanel'

const LARGURA_DA_ALCA = 6
const PASSO_DO_TECLADO = 16
const DURACAO_DA_TRANSICAO_MS = 200
/** A borda esquerda da coluna, que separa o painel do conteúdo da tela. */
const BORDA = 1

/** Avisa quem mede a própria largura, o mapa entre eles, que a coluna mudou. O
 *  mapa já observa o container por ResizeObserver e acompanha a transição; o
 *  evento cobre quem escuta só a janela. */
function avisarRedimensionamento() {
  window.dispatchEvent(new Event('resize'))
}

function Alca({ largura, onLargura, onArrastando }: {
  largura: number
  onLargura: (n: number) => void
  onArrastando: (arrastando: boolean) => void
}) {
  const { t } = useI18n()
  const origemRef = useRef<{ x: number; largura: number } | null>(null)

  function aoPressionar(e: PointerEvent<HTMLDivElement>) {
    if (e.button !== 0) return
    origemRef.current = { x: e.clientX, largura }
    e.currentTarget.setPointerCapture(e.pointerId)
    onArrastando(true)
  }
  function aoMover(e: PointerEvent<HTMLDivElement>) {
    const origem = origemRef.current
    if (!origem) return
    // Arrastar para a esquerda alarga: a alça é a borda esquerda da coluna.
    onLargura(limitarLargura(origem.largura + (origem.x - e.clientX)))
  }
  function aoSoltar(e: PointerEvent<HTMLDivElement>) {
    if (!origemRef.current) return
    origemRef.current = null
    e.currentTarget.releasePointerCapture(e.pointerId)
    onArrastando(false)
    avisarRedimensionamento()
  }
  function aoTeclar(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'ArrowLeft') onLargura(limitarLargura(largura + PASSO_DO_TECLADO))
    else if (e.key === 'ArrowRight') onLargura(limitarLargura(largura - PASSO_DO_TECLADO))
    else return
    e.preventDefault()
  }

  return (
    <Box
      role="separator"
      aria-orientation="vertical"
      aria-label={t('assistente.painel.redimensionar')}
      aria-valuemin={LARGURA_MIN}
      aria-valuemax={LARGURA_MAX}
      aria-valuenow={largura}
      tabIndex={0}
      onPointerDown={aoPressionar}
      onPointerMove={aoMover}
      onPointerUp={aoSoltar}
      onPointerCancel={aoSoltar}
      onKeyDown={aoTeclar}
      sx={{
        position: 'absolute',
        top: 0,
        bottom: 0,
        left: 0,
        width: LARGURA_DA_ALCA,
        cursor: 'col-resize',
        zIndex: 2,
        touchAction: 'none',
        outline: 'none',
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: 0,
          width: 2,
          bgcolor: 'primary.main',
          opacity: 0,
          transition: 'opacity var(--transition-quick)',
        },
        '&:hover::after, &:focus-visible::after, &[data-arrastando="true"]::after': { opacity: 1 },
      }}
    />
  )
}

export function AssistenteColuna() {
  const { ativo, aberto, largura, setLargura, fechar } = useAssistente()
  const { t } = useI18n()
  const tema = useTheme()
  // noSsr: a SPA é só cliente; avaliar já no primeiro render evita o painel
  // piscar como gaveta antes de virar coluna.
  const empurra = useMediaQuery(tema.breakpoints.up('md'), { noSsr: true })
  const [arrastando, setArrastando] = useState(false)
  // O conteúdo fica montado durante a transição de fechar, para a coluna
  // encolher com o painel dentro em vez de esvaziar de repente.
  const [montado, setMontado] = useState(aberto)

  useEffect(() => {
    if (aberto) {
      setMontado(true)
      return
    }
    const id = setTimeout(() => setMontado(false), DURACAO_DA_TRANSICAO_MS + 40)
    return () => clearTimeout(id)
  }, [aberto])

  if (!ativo) return null

  if (!empurra) {
    return (
      <Drawer
        variant="temporary"
        anchor="right"
        open={aberto}
        onClose={fechar}
        PaperProps={{ component: 'aside', 'aria-label': t('assistente.painel.rotulo') }}
        sx={{ '& .MuiDrawer-paper': { width: '100%', maxWidth: '100%', boxSizing: 'border-box' } }}
      >
        <AssistentePanel />
      </Drawer>
    )
  }

  function aoTerminarTransicao(e: TransitionEvent<HTMLElement>) {
    if (e.target === e.currentTarget && e.propertyName === 'width') avisarRedimensionamento()
  }

  return (
    <Box
      component="aside"
      aria-label={t('assistente.painel.rotulo')}
      aria-hidden={!aberto}
      onTransitionEnd={aoTerminarTransicao}
      sx={{
        // A coluna acompanha a rolagem da página presa ao topo, e quem gruda é o
        // PRÓPRIO aside, não um filho dele. O `overflow: hidden` que recorta o
        // painel enquanto a largura anima faz do aside um contêiner de rolagem, e
        // um sticky dentro dele nunca gruda em nada: rolar o Painel de Jornada
        // levava o assistente embora junto com a página. 05/09/2026.
        position: 'sticky',
        top: 0,
        alignSelf: 'flex-start',
        height: '100dvh',
        // A largura da coluna soma a borda à do painel. Sem isso o painel de
        // 400 px ficava com 399 px de interior, em border-box, e perdia o último
        // pixel no recorte. Somar aqui é melhor que trocar o box-sizing: o reset
        // do CssBaseline manda `box-sizing: inherit` em tudo, então content-box
        // no aside vazaria para o painel inteiro.
        width: aberto ? largura + BORDA : 0,
        flexShrink: 0,
        overflow: 'hidden',
        transition: arrastando ? 'none' : 'width .2s ease',
        borderLeft: aberto ? '1px solid' : 'none',
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      {montado && (
        // Largura fixa em px, e não 100%, para o conteúdo não reflowar durante a
        // transição: a coluna abre por cima de um painel já do tamanho final.
        <Box sx={{ position: 'relative', height: '100%', width: largura, display: 'flex' }} data-arrastando={arrastando}>
          <Alca largura={largura} onLargura={setLargura} onArrastando={setArrastando} />
          <AssistentePanel />
        </Box>
      )}
    </Box>
  )
}
